import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Mail } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Landing page for guest membership purchases. Polls the checkout-status
// endpoint (unauthenticated) until Stripe reports "paid", at which point the
// backend has minted the user account + emailed a set-password link.
export default function MembershipWelcomePage() {
    const [params] = useSearchParams();
    const sessionId = params.get('session_id') || '';
    const [state, setState] = useState({ status: 'pending', tier: '', email: '' });

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        let attempts = 0;
        const poll = async () => {
            attempts += 1;
            try {
                const { data } = await axios.get(`${API_URL}/api/membership/checkout/status/${sessionId}`);
                if (cancelled) return;
                if (data.payment_status === 'paid') {
                    setState({ status: 'paid', tier: data.tier || '', email: data.user_email || '' });
                    return;
                }
                if (attempts < 12) setTimeout(poll, 1500);
                else setState((s) => ({ ...s, status: 'timeout' }));
            } catch (_err) {
                if (!cancelled) toast.error("Couldn't confirm your payment. Please contact support if this persists.");
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <Navbar />
            <main className="pt-24 pb-16" data-testid="membership-welcome-page">
                <div className="max-w-xl mx-auto px-6 sm:px-8 text-center">
                    {state.status === 'paid' ? (
                        <>
                            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
                            <p className="overline mb-3">Welcome</p>
                            <h1 className="heading-2 mb-4">Your account is ready.</h1>
                            <p className="body-text mb-6">
                                Payment received. We just emailed <strong>{state.email}</strong> with a link to set your password —
                                once you've done that, you can sign in and start using your{state.tier ? ' ' + state.tier : ''} membership.
                            </p>
                            <div className="p-4 bg-white border border-[#E5E0D8] rounded-sm text-left text-sm text-[#4A4A68] mb-6 flex items-start gap-3">
                                <Mail className="w-5 h-5 text-[#5B2D8E] mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-[#1A1A2E] font-medium mb-1">Check your inbox (and spam)</p>
                                    <p>If the welcome email hasn't arrived in a few minutes, check your promotions or spam folder. You can also use <Link to="/forgot-password" className="text-[#5B2D8E] hover:underline">Forgot password</Link> on the sign-in page to receive a fresh link.</p>
                                </div>
                            </div>
                            <Link to="/login" className="btn-primary inline-block" data-testid="welcome-signin">Sign in</Link>
                        </>
                    ) : state.status === 'timeout' ? (
                        <>
                            <p className="overline mb-3">Just a moment</p>
                            <h1 className="heading-2 mb-4">Your payment is being confirmed</h1>
                            <p className="body-text mb-6">Stripe is taking a bit longer than usual to confirm. Once done, we'll email you a link to set your password.</p>
                            <Link to="/login" className="btn-secondary inline-block">Try signing in</Link>
                        </>
                    ) : (
                        <>
                            <Loader2 className="w-10 h-10 text-[#5B2D8E] mx-auto mb-4 animate-spin" />
                            <p className="overline mb-3">Please wait</p>
                            <h1 className="heading-2 mb-4">Confirming your payment…</h1>
                            <p className="body-text">This only takes a few seconds.</p>
                        </>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}
